import React, { createContext, useReducer, useEffect } from 'react';
import useReactRouter from 'use-react-router';
import * as api from 'da-pcc-frontend-accountmanagement-api';
import { usePageError } from './ErrorContext';
import withAPI from '../api';
import { useProfileContext } from './ProfileContext';
import Loader from '../Common/Loader';
import { useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';

const InitialState = {
  dataLoaded: false,
  accountPermissions: null as Set<api.AccountPermission>,
  account: {} as api.AccountDetail,
};

type AccountState = typeof InitialState;

type AccountContextAction =
  {
    type: "SETACCOUNT",
    accountPermissions: Set<api.AccountPermission>,
    account: api.AccountDetail
  }
  | {
    type: "UPDATEACCOUNT",
    account: api.AccountDetail
  }

/**
 * Reducer to create new state with updated information
 */
const reducer = (state: AccountState, action: AccountContextAction): AccountState => {
  switch (action.type) {

    case "SETACCOUNT":
      return {
        ...state,
        dataLoaded: true,
        accountPermissions: action.accountPermissions,
        account: action.account,
      };

    case "UPDATEACCOUNT": {
      return {
        ...state,
        account: action.account
      }
    }
  }
};

/** Account Context */
const AccountContext = createContext<[AccountState, (action: AccountContextAction) => void]>([InitialState, null]);

/** Load preferred account */
const loadPreferredAccount = async (profile: api.UserProfile, id = "") => {
  // Load from profile
  let account: api.AccountDetail;

  if (id) {
    account = await withAPI(api.AccountsApi).getAccountsId({ id });
  }
  else {
    account = profile.preferredAccount;
  }

  if (!account)
    throw Error("No account");

  return {
    accountPermissions: new Set(account.permissions),
    account,
  }
};

/** Account Context Provider */
const AccountContextProvider = ({ children, fallback = <Loader /> }) => {
  const { match } = useReactRouter();
  const showPageError = usePageError();
  const value = useReducer(reducer, InitialState);
  const profile = useProfileContext();
  const [state, dispatch] = value;
  const id = match.params["id"];
  const [m] = useTranslation("marketsegments");

  useEffect(() => {
    if (!profile.loaded)
      return;

    let update = dispatch;

    loadPreferredAccount(profile, id)
      .then(preferred => {
        // update Google Analytics dimensions to reflect new account info
        const { account } = preferred;
        const gaAccountId = (account && account.id) ? account.id : undefined;
        const gaAccountType = (account && account.type) ? account.type : undefined;
        let gaMarketSegment = (account && account.market) ? account.market : undefined;

        // if account has a market segment defined, use translated market segment name
        // for gaMarketSegment value
        if (gaMarketSegment) {
          const marketSegments = m("applications");
          gaMarketSegment = marketSegments[account.market];
        }

        TagManager.dataLayer({
          dataLayer: {
            accountType: gaAccountType,
            accountId: gaAccountId,
            marketSegment: gaMarketSegment,
          }
        });

        return (update && update({
          type: "SETACCOUNT",
          ...preferred
        }));
      })
      .catch(err => {
        if (err instanceof Response) {
          if (err.status < 500)
            showPageError("Account Not Found");
          else
            showPageError("Server Error");
        }
        else
          showPageError("No Accounts");
      });

    return () => update = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile]);

  if (state.dataLoaded)
    return (
      <AccountContext.Provider value={value}>
        {children}
      </AccountContext.Provider>
    );
  else
    return fallback;
}

export default AccountContext;
export {
  AccountContextProvider
};